"use client";

import type { Tier } from "@mw/shared/pricing";
import type { Locale } from "@mw/shared/i18n";

import { useCurrency } from "@/app/_providers/currency";
import { formatNumberToKM } from "@utilities/numbers";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { useState, useMemo, useCallback } from "react";
import { useDebounceValue } from "usehooks-ts";
import { getPricingCardContent, getPricingTiers, getTiers } from "@mw/shared/pricing";
import { TagGroup, TagList, Tag, Button as AriaButton, MenuTrigger } from "react-aria-components";
import { m, AnimatePresence } from "motion/react";

import { Noise } from "../../core/Noise/Noise";
import { ListBox, type ItemBase, type SectionBase } from "../../core/ListBox";
import { getFormattedPrices, TIER_COVER_IMAGES } from "../utils";
import clsx from "clsx";
import IconCircleInfo from "@ui/central-icons/icons/IconCircleInfo";
import IconCircleCheck from "@ui/central-icons/icons/IconCircleCheck";
import IconCrossLarge from "@ui/central-icons/icons/IconCrossLarge";
import { LinkTracked } from "../../core/LinkTracked";
import { EventName } from "@mw/shared/analyticsEventNames";
import IconPlusLarge from "@ui/central-icons/icons/IconPlusLarge";

import {
  type CalculatorFeatureId,
  type AgentsFeatureId,
  type CalculatorFeature,
  type FeatureCategory,
  CREDITS_PER_MINUTE,
  sliderToMinutes,
  minutesToSlider,
  minutesToCredits,
  useMinutesSlider,
  getMinTierForFeatures,
  getMinTierForAgentFeatures,
  getRecommendedTier,
  getRecommendedTierForAgents,
  deriveFeatures,
  getSelectableTiers,
  getBusinessMaxMinutes,
  getAgentsMaxMinutes,
  getAgentMinutesByTier,
  FEATURE_DEFINITIONS,
  AGENT_FEATURE_DEFINITIONS,
} from "./calculatorUtils";
import { MinutesSlider } from "./MinutesSlider";
import { StepIndicator } from "./StepIndicator";
import { usePricingTableContext } from "../PricingTableContext";

export type CalculatorVariant = "default" | "agents";

/** Configuration for a section in the feature selector */
interface SectionConfig {
  id: string;
  label: string;
  category: FeatureCategory;
}

interface FeatureSelectorProps<T extends string> {
  /** All available features */
  features: CalculatorFeature<T>[];
  /** Currently selected feature IDs */
  selectedFeatures: Set<T>;
  /** Callback when selection changes */
  onSelectionChange: (features: Set<T>) => void;
  /** Function to get translated label for a feature */
  getLabel: (featureId: T) => string;
  /** Sections to group features by category */
  sections: SectionConfig[];
}

function FeatureSelector<T extends string>({
  features,
  selectedFeatures,
  onSelectionChange,
  getLabel,
  sections,
}: FeatureSelectorProps<T>) {
  const t = useTranslations("PricingClientKeys.PricingCard");

  const handleRemove = useCallback(
    (keys: Set<React.Key>) => {
      const newSelection = new Set(selectedFeatures);
      keys.forEach(key => newSelection.delete(key as T));
      onSelectionChange(newSelection);
    },
    [selectedFeatures, onSelectionChange],
  );

  const handleAdd = useCallback(
    (featureId: T) => {
      const newSelection = new Set(selectedFeatures);
      newSelection.add(featureId);
      onSelectionChange(newSelection);
    },
    [selectedFeatures, onSelectionChange],
  );

  // Preserve insertion order by iterating over selectedFeatures Set
  const selectedFeatureObjects = useMemo(() => {
    const featureMap = new Map(features.map(f => [f.id, f]));
    return Array.from(selectedFeatures)
      .map(id => featureMap.get(id))
      .filter((f): f is CalculatorFeature<T> => f !== undefined);
  }, [features, selectedFeatures]);

  // Group available features into sections for the ListBox
  const listBoxSections = useMemo(() => {
    const available = features.filter(f => !selectedFeatures.has(f.id));

    const result: SectionBase<ItemBase>[] = [];

    for (const section of sections) {
      const sectionFeatures = available
        .filter(f => f.category === section.category)
        .sort((a, b) => getLabel(a.id).localeCompare(getLabel(b.id)))
        .map(f => ({
          id: f.id,
          textValue: getLabel(f.id),
          action: () => handleAdd(f.id),
        }));

      if (sectionFeatures.length > 0) {
        result.push({ id: section.id, label: section.label, items: sectionFeatures });
      }
    }

    return result;
  }, [features, selectedFeatures, sections, getLabel, handleAdd]);

  return (
    <div className='tw-flex tw-flex-wrap tw-gap-2 tw-items-start'>
      <TagGroup
        aria-label={t("calculator.selected_features_label")}
        selectionMode='none'
        onRemove={handleRemove}
        className='tw-contents'
      >
        <TagList items={selectedFeatureObjects} className='tw-contents'>
          {feature => (
            <Tag
              id={feature.id}
              textValue={getLabel(feature.id)}
              className='tw-inline-flex tw-items-center tw-gap-2 tw-h-11 tw-py-2 tw-pl-4 tw-pr-2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-text-base tw-transition-colors hover:tw-bg-gray-50 hover:tw-border-gray-300 focus-visible:tw-outline-focus tw-cursor-default'
            >
              {({ allowsRemoving }) => (
                <>
                  <span>{getLabel(feature.id)}</span>
                  {allowsRemoving && (
                    <AriaButton
                      slot='remove'
                      className='tw-relative tw-flex tw-items-center tw-justify-center tw-size-11 tw--my-2 tw--mr-2 tw--ml-2 tw-transition-colors focus-visible:tw-outline-focus before:tw-absolute before:tw-size-4 before:tw-rounded before:tw-transition-colors hover:before:tw-bg-gray-200'
                    >
                      <IconCrossLarge className='tw-size-3 tw-relative' />
                    </AriaButton>
                  )}
                </>
              )}
            </Tag>
          )}
        </TagList>
      </TagGroup>

      {/* Add feature button with menu */}
      {listBoxSections.length > 0 && (
        <MenuTrigger>
          <AriaButton className='tw-inline-flex tw-items-center tw-gap-2 tw-h-11 tw-py-2 tw-px-4 tw-bg-gray-100 tw-border tw-border-gray-200 tw-rounded-xl tw-text-base tw-text-gray-600 tw-transition-colors hover:tw-bg-gray-200 focus-visible:tw-outline-focus'>
            <IconPlusLarge className='tw-size-3' />
            <span>{t("calculator.add_feature")}</span>
          </AriaButton>

          <ListBox
            sections={listBoxSections}
            aria-label={t("calculator.available_features_label")}
            asMenu
            placement='bottom start'
            maxHeight={335}
          />
        </MenuTrigger>
      )}
    </div>
  );
}

interface RecommendedTierCardProps {
  tier: Tier;
  billingCycle: "monthly" | "yearly";
  pricingCardContent: ReturnType<typeof getPricingCardContent>;
}

function RecommendedTierCard({ tier, billingCycle, pricingCardContent }: RecommendedTierCardProps) {
  const t = useTranslations("PricingClientKeys.PricingCard");
  const locale = useLocale() as Locale;
  const { currency, currencySymbol } = useCurrency();

  const isEnterprise = tier.planId === "enterprise";

  const contentKey = tier.planId;
  const tierContent = pricingCardContent[contentKey as keyof typeof pricingCardContent];
  const platformContent = tierContent.simple;

  const { price, discountedPrice, discountMessageKey, formattedOriginalPrice, currencyFormattedPrice } =
    getFormattedPrices(tier.pricing, billingCycle, currency, currencySymbol, locale);

  const coverImage = TIER_COVER_IMAGES[tier.planId];
  const hasDiscount = discountedPrice && price && formattedOriginalPrice && discountMessageKey;
  const tierName = t(platformContent.title);

  return (
    <div className='tw-relative lg:tw-aspect-square tw-w-full'>
      <div className='tw-relative lg:tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-2xl'>
        <div className='tw-absolute tw-inset-0 tw-w-full tw-h-full tw-bg-cream' />

        {coverImage && (
          <div className='tw-absolute tw-inset-0 tw-w-full tw-h-full tw-opacity-100'>
            <Image src={coverImage} alt='' fill className='tw-object-cover' quality={90} placeholder='blur' />
          </div>
        )}

        <Noise className='tw-rounded-2xl' />

        <div className='tw-relative tw-p-5 lg:tw-p-10 tw-flex tw-flex-col tw-h-full tw-z-10'>
          {/* Header: Title + Price */}
          <div className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-between sm:tw-items-start tw-gap-3 sm:tw-gap-0 tw-mb-4 lg:tw-mb-6'>
            {/* Left: Title + Recommended Plan */}
            <div>
              <h3 className='tw-text-white tw-type-5xl tw-mb-2'>{tierName}</h3>
              <p className='tw-text-white/60 tw-type-xs'>{t("calculator.recommended_plan")}</p>
            </div>

            {/* Right: Price */}
            {!isEnterprise ? (
              <div className='sm:tw-text-right tw-text-white'>
                <div className='tw-flex tw-items-baseline tw-gap-1 sm:tw-justify-end tw-mb-2'>
                  <span className='tw-type-5xl'>{currencyFormattedPrice}</span>
                  {hasDiscount && (
                    <span className='tw-line-through tw-opacity-50 tw-type-5xl'>{formattedOriginalPrice}</span>
                  )}
                </div>
                <p className='tw-text-white/60 tw-type-xs'>{t("period_month_long")}</p>
              </div>
            ) : (
              <div className='sm:tw-text-right'>
                <span className='tw-text-white tw-type-lg'>{t("custom_pricing")}</span>
              </div>
            )}
          </div>

          {/* Feature list */}
          <div className='tw-space-y-2 lg:tw-space-y-3 tw-mb-6 lg:tw-mb-0'>
            {/* Discount notice if available */}
            {hasDiscount && (
              <div className='tw-flex tw-items-center tw-gap-2 tw-text-white'>
                <IconCircleInfo className='tw-size-5 tw-flex-shrink-0' />
                <span className='tw-type-base first-letter:tw-uppercase'>{t(discountMessageKey)}</span>
              </div>
            )}

            {/* Cancel anytime */}
            <div className='tw-flex tw-items-center tw-gap-2 tw-text-white'>
              <IconCircleCheck className='tw-size-5 tw-flex-shrink-0' />
              <span className='tw-type-base'>{t("calculator.pause_or_cancel_anytime")}</span>
            </div>

            {/* Credits included */}
            <div className='tw-flex tw-items-center tw-gap-2 tw-text-white'>
              <IconCircleCheck className='tw-size-5 tw-flex-shrink-0' />
              <span className='tw-type-base'>
                {isEnterprise
                  ? t("custom_number_of_credits")
                  : t("calculator.credits_every_month", { credits: formatNumberToKM(tier.pricing.includedCredits) })}
              </span>
            </div>
          </div>

          {/* CTA Button */}
          <div className='tw-mt-auto lg:-tw-mx-5 lg:-tw-mb-5'>
            <LinkTracked
              eventName={EventName.mw_pricing_card_cta_clicked}
              eventProperties={{
                tier: tier.planId,
                source: "pricing_calculator",
              }}
              asButton
              buttonVariants={{
                variant: "elevated",
                size: "lg",
                expand: true,
              }}
              className='!tw-rounded-2xl'
              href={tier.cta?.href || "/app/sign-up"}
              label={t("cta_label_choose", { tier: tierName })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export interface PricingCalculatorProps {
  className?: string;
  defaultBillingCycle?: "monthly" | "yearly";
  /** Calculator variant - "default" shows feature selector, "agents" shows only minutes slider */
  variant?: CalculatorVariant;
  /** Initial minutes when mounting. Converted to slider position internally. */
  initialMinutes?: number;
  /** Initial features selection when mounting. */
  initialFeatures?: Set<string>;
  /** Called with actual minutes when slider changes, for external state sync. */
  onMinutesChange?: (minutes: number) => void;
  /** Called when feature selection changes, for external state sync. */
  onFeaturesChanged?: (features: Set<string>) => void;
}

/** Configuration for a calculator variant */
interface VariantConfig {
  maxMinutes: number;
  features: CalculatorFeature<string>[];
  defaultFeatures: Set<string>;
  sectionConfigs: Array<{ id: string; labelKey: string; category: FeatureCategory }>;
  featureLabelKey: string;
  stepOneLabel: string;
  sliderLabel: string;
  getMinTierMinutes: (features: string[]) => number;
  getRecommendedTier: (minutes: number, features: string[]) => Tier;
}

/** Static parts of each variant config that don't depend on the tier set */
const VARIANT_STATIC = {
  default: {
    defaultFeatures: new Set(["text_to_speech"]),
    sectionConfigs: [
      { id: "products", labelKey: "calculator.section_products", category: "product" as FeatureCategory },
      { id: "capabilities", labelKey: "calculator.section_capabilities", category: "capability" as FeatureCategory },
    ],
    featureLabelKey: "calculator.features",
    stepOneLabel: "calculator.select_features_and_capabilities",
    sliderLabel: "calculator.estimated_minutes_per_month",
  },
  agents: {
    defaultFeatures: new Set([
      "agents_workflow_builder",
      "agents_tool_calling",
      "agents_guardrails",
      "agents_knowledge_base",
      "agents_multilingual",
      "agents_widget",
    ]),
    sectionConfigs: [
      { id: "included", labelKey: "calculator.section_included", category: "included" as FeatureCategory },
      { id: "capabilities", labelKey: "calculator.section_capabilities", category: "capability" as FeatureCategory },
    ],
    featureLabelKey: "calculator.agents_features",
    stepOneLabel: "calculator.select_agent_features",
    sliderLabel: "calculator.minutes_of_calls_per_month",
  },
} satisfies Record<CalculatorVariant, Partial<VariantConfig>>;

function buildVariantConfigs(): Record<CalculatorVariant, VariantConfig> {
  const pricingTiers = getPricingTiers();
  const tiersById = getTiers();
  const selectableTiers = getSelectableTiers(pricingTiers);
  const calcFeatures = deriveFeatures(FEATURE_DEFINITIONS, pricingTiers);
  const agentFeatures = deriveFeatures(AGENT_FEATURE_DEFINITIONS, pricingTiers);
  const agentMinutesByTier = getAgentMinutesByTier(pricingTiers);
  const businessMaxMinutes = getBusinessMaxMinutes(tiersById);
  const agentsMaxMinutes = getAgentsMaxMinutes(tiersById);

  return {
    default: {
      ...VARIANT_STATIC.default,
      maxMinutes: businessMaxMinutes,
      features: calcFeatures,
      getMinTierMinutes: features => {
        const minTier = getMinTierForFeatures(features as CalculatorFeatureId[], pricingTiers, calcFeatures);
        return minTier.pricing.includedCredits / CREDITS_PER_MINUTE;
      },
      getRecommendedTier: (minutes, features) =>
        getRecommendedTier(features as CalculatorFeatureId[], minutesToCredits(minutes), pricingTiers, calcFeatures),
    },
    agents: {
      ...VARIANT_STATIC.agents,
      maxMinutes: agentsMaxMinutes,
      features: agentFeatures,
      getMinTierMinutes: features => {
        const minTier = getMinTierForAgentFeatures(features as AgentsFeatureId[], pricingTiers, agentFeatures);
        return agentMinutesByTier[minTier.planId] || 0;
      },
      getRecommendedTier: (minutes, features) =>
        getRecommendedTierForAgents(
          minutes,
          features as AgentsFeatureId[],
          pricingTiers,
          agentFeatures,
          agentMinutesByTier,
        ),
    },
  };
}

export function PricingCalculator({
  className,
  defaultBillingCycle,
  variant = "default",
  initialMinutes,
  initialFeatures,
  onMinutesChange,
  onFeaturesChanged,
}: PricingCalculatorProps) {
  const t = useTranslations("PricingClientKeys.PricingCard");
  const { billingCycle: contextBillingCycle } = usePricingTableContext();
  const billingCycle = defaultBillingCycle ?? contextBillingCycle ?? "monthly";
  const variantConfigs = useMemo(() => buildVariantConfigs(), []);
  const config = variantConfigs[variant];
  const pricingCardContent = getPricingCardContent();

  // Feature selection state
  const [selectedFeatures, setSelectedFeatures] = useState<Set<string>>(
    () => initialFeatures ?? new Set(config.defaultFeatures),
  );

  const {
    sliderValue,
    setSliderValue,
    minutes: minutesUsage,
    handleSliderChange,
  } = useMinutesSlider(initialMinutes ?? 50, config.maxMinutes, onMinutesChange);

  const getFeatureLabel = useCallback(
    (featureId: string) => t(`${config.featureLabelKey}.${featureId}` as any),
    [t, config.featureLabelKey],
  );

  // Translate section labels
  const sections = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => config.sectionConfigs.map(s => ({ id: s.id, label: t(s.labelKey as any), category: s.category })),
    [t, config.sectionConfigs],
  );

  // Handle feature selection changes - also update slider if needed
  const handleFeaturesChange = useCallback(
    (newFeatures: Set<string>) => {
      setSelectedFeatures(newFeatures);
      onFeaturesChanged?.(newFeatures);

      // Get the minimum tier required for the new feature set
      const minTierMinutes = config.getMinTierMinutes(Array.from(newFeatures));
      const minSliderValue = minutesToSlider(minTierMinutes, config.maxMinutes);

      // If current slider is below the tier's included amount, bump it up
      setSliderValue(current => {
        const next = Math.max(current, minSliderValue);
        if (next !== current) onMinutesChange?.(sliderToMinutes(next, config.maxMinutes));
        return next;
      });
    },
    [config, onFeaturesChanged, onMinutesChange],
  );

  // Debounce the minutes usage to avoid jarring tier changes while sliding
  const [debouncedMinutesUsage] = useDebounceValue(minutesUsage, 50);

  const recommendedTier = useMemo(
    () => config.getRecommendedTier(debouncedMinutesUsage, Array.from(selectedFeatures)),
    [config, selectedFeatures, debouncedMinutesUsage],
  );

  // Get tier name for live region announcement
  const tierContent = pricingCardContent[recommendedTier.planId as keyof typeof pricingCardContent];
  const recommendedTierName = t(tierContent.simple.title);

  return (
    <div className={clsx("tw-w-full", className)}>
      <div className='tw-flex tw-flex-col lg:tw-flex-row tw-gap-8 lg:tw-gap-16'>
        {/* Left side - Feature selection and credit slider */}
        <div className='tw-w-full lg:tw-w-1/2 tw-space-y-8 tw-order-2 lg:tw-order-1'>
          {/* Step 1: Feature selection */}
          <div>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <StepIndicator step={1} label={t(config.stepOneLabel as any)} />

            <FeatureSelector
              features={config.features}
              selectedFeatures={selectedFeatures}
              onSelectionChange={handleFeaturesChange}
              getLabel={getFeatureLabel}
              sections={sections}
            />
          </div>

          {/* Step 2: Minutes usage slider */}
          <div>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <StepIndicator step={2} label={t(config.sliderLabel as any)} />

            <MinutesSlider
              sliderValue={sliderValue}
              onSliderChange={handleSliderChange}
              maxMinutes={config.maxMinutes}
              ariaLabel={t(config.sliderLabel as any)} // eslint-disable-line @typescript-eslint/no-explicit-any
            />
          </div>
        </div>

        {/* Right side - Recommended tier card */}
        <div className='tw-w-full lg:tw-w-1/2 tw-order-1 lg:tw-order-2'>
          {/* Visually hidden live region for screen reader announcements */}
          <div className='tw-sr-only' aria-live='polite' aria-atomic='true'>
            {t("calculator.tier_recommendation_announcement", { tierName: recommendedTierName })}
          </div>

          <AnimatePresence mode='popLayout' initial={false}>
            <m.div
              key={recommendedTier.planId}
              layout
              layoutId='recommended-tier-card'
              initial={{ opacity: 0.7 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0.7 }}
              transition={{ duration: 0.2, delay: 0.1 }}
            >
              <RecommendedTierCard
                tier={recommendedTier}
                billingCycle={billingCycle}
                pricingCardContent={pricingCardContent}
              />
            </m.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
